import { FieldErrors } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { Tab } from '../../Common/Components/Tabs/Types'
import { TelSysConfigurationModalID } from '../Constants/Constants'
import { TelSysForm } from '../Types/Types'

/**
 * Per-tab validation error flags, keyed by tab ID.
 *
 * Keyed rather than positional on purpose: the tabs are read by
 * TelSysConfigurationModalID, so a positional array silently mismaps every tab after any
 * ID that has no entry (APD-13690).
 */
export type TabValidationErrors = Record<TelSysConfigurationModalID, boolean>

/**
 * The subset of a tab needed to decide which tab to focus: its ID and whether the user can
 * currently see it.
 */
export type TabDisplayState = Pick<Tab, 'ID' | 'isDisplayed' | 'isRemoved'>

/**
 * Maps react-hook-form's form error groups onto the tab that contains them.
 *
 * Every member of TelSysConfigurationModalID must appear here. The form group names
 * mirror the top-level groups of TelSysForm.
 *
 * @param errors Form errors from react-hook-form, one entry per form group holding an error.
 * @returns A flag per tab ID, true when that tab holds at least one field error.
 */
export const mapTabValidationErrors = (errors: FieldErrors<TelSysForm>): TabValidationErrors => ({
    [TelSysConfigurationModalID.TELSYS_DETAILS]: !!errors.details,
    [TelSysConfigurationModalID.TELSYS_COMMUNICATIONS]: !!errors.communications,
    [TelSysConfigurationModalID.TELSYS_SYNC]: !!errors.sync,
    [TelSysConfigurationModalID.TELSYS_DEFAULT_SITE_SETTINGS]: !!errors.defaultSiteSettings,
    [TelSysConfigurationModalID.TELSYS_LICENSE_MANAGEMENT]: !!errors.licenseManagement,
    [TelSysConfigurationModalID.TELSYS_ADVANCED]: !!errors.advanced
})

/**
 * Finds the tab to focus when an invalid form is submitted.
 *
 * Returns the tab's ID, not its position in the array. The two are not
 * interchangeable, and conflating them focused the wrong tab (APD-13690). Tabs hidden
 * from the user are skipped, using the same rule as the Tabs component.
 *
 * The error flags are taken as an argument rather than read off the tabs so that callers
 * can pass the errors react-hook-form reports at submit time, which are newer than the
 * flags held by the tabs rendered for the previous state.
 *
 * @param tabs Tabs in display order.
 * @param validationErrors Error flag per tab ID, from mapTabValidationErrors.
 * @returns The ID of the first displayed tab holding an error, or undefined when none do.
 */
export const getFirstTabIDWithError = (
    tabs: TabDisplayState[],
    validationErrors: TabValidationErrors
): Definable<TelSysConfigurationModalID> =>
    tabs.find(
        ({ ID, isDisplayed, isRemoved }: TabDisplayState): boolean =>
            validationErrors[ID as TelSysConfigurationModalID] && (isDisplayed ?? true) && !isRemoved
    )?.ID
